import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ColDef } from "ag-grid-community";
import { Search, Filter, Trash2, CheckCircle } from "lucide-react";
import ConfirmModal from "../../components/modal/ConfirmModal";
import images from "../../assets/images";
import Filters from "./Filters";
import AddTask from "./AddTask";
import "../../styles/ag-grid-custom.css";
import { Task } from "../../types/api";
import {
  useEquipmentStore,
  useOrgUsersStore,
  useTaskStore,
  usePremisesStore,
} from "../../store/store";
import { DotLoader, SpinLoader } from "../../components/loader/Loader";
import axiosInstance from "../../common/axiosInstance";
import { usePermissions } from "../../hooks/usePermission";
import { TASK__MODULE } from "../../constants/modules";
import { toast } from "react-toastify";

interface TaskTableProps {
  selectedPremises: string;
  selectedRegion?: string;
  selectedModule?: string;
}

const TaskTable: React.FC<TaskTableProps> = ({
  selectedPremises,
  selectedRegion,
  selectedModule,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);

  const {
    tasks,
    loading,
    error,
    fetchTasks,
    resetFilter,
    tasksWithFilter,
    deleteTask,
    searchTasks,
    setCurrentTask,
  } = useTaskStore();

  const { canRead, canUpdate, canDelete } = usePermissions();
  const { fetchOrgUsers, orgUsersData } = useOrgUsersStore();
  const { equipmentData, allAssetsEquipment } = useEquipmentStore();
  const { allPremises, fetchPremises } = usePremisesStore();

  const gridRef = useRef<AgGridReact<Task>>(null);

  useEffect(() => {
    fetchTasks({
      regionId: selectedRegion,
      premisesId: selectedPremises,
    });

    allAssetsEquipment();
    fetchOrgUsers();

    if (!allPremises.length) fetchPremises();
  }, [fetchTasks, allAssetsEquipment, selectedRegion, selectedPremises, fetchOrgUsers, allPremises.length, fetchPremises]);

  const filteredTasks = React.useMemo(() => {
    let filtered = tasks;

    // Filter by region if selected and premises is not selected
    if (selectedRegion && !selectedPremises) {
      const premisesInRegion = allPremises
        .filter((p) => p.region === selectedRegion)
        .map((p) => p.id);
      filtered = filtered.filter((task) => !!task.premisesId && premisesInRegion.includes(task.premisesId));
    }

    // Filter by premises if selected
    if (selectedPremises) {
      filtered = filtered.filter(
        (task) => task.premisesId === selectedPremises
      );
    }

    // Filter by module if selected (empty string means show all)
    if (selectedModule) {
      filtered = filtered.filter((task) => task.module === selectedModule);
    }

    return filtered;
  }, [tasks, selectedRegion, selectedPremises, selectedModule, allPremises]);

  const formatBeginningDate = useCallback((dateStr?: string) => {
    if (!dateStr) return "N/A";

    // If it's already slash-formatted, return as-is
    const slashDatePatterns = [
      /^\d{1,2}\/\d{1,2}\/\d{4}$/,
      /^\d{4}\/\d{1,2}\/\d{1,2}$/,
    ];
    if (slashDatePatterns.some((re) => re.test(dateStr))) {
      return dateStr;
    }

    // Use local date instead of UTC to avoid timezone issues
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "N/A";

    const day = String(date.getDate()).padStart(2, "0"); // getDate() instead of getUTCDate()
    const month = String(date.getMonth() + 1).padStart(2, "0"); // getMonth() instead of getUTCMonth()
    const year = date.getFullYear(); // getFullYear() instead of getUTCFullYear()

    return `${day}/${month}/${year}`;
  }, []);

  const handleView = useCallback(
    (task: Task) => {
      setCurrentTask(task);
      setIsViewModalOpen(true);
    },
    [setCurrentTask]
  );

  const handleEdit = useCallback(
    (task: Task) => {
      setCurrentTask(task);
      setIsEditModalOpen(true);
    },
    [setCurrentTask]
  );

  const handleDeleteClick = useCallback(
    (task: Task) => {
      setTaskToDelete(task);
      setIsDeleteModalOpen(true);
    },
    []
  );

  const handleDeleteConfirm = useCallback(
    async () => {
      if (!taskToDelete) return;
      try {
        await deleteTask(taskToDelete.id);
      } catch (error) {
        console.error("Error deleting task:", error);
      } finally {
        setIsDeleteModalOpen(false);
        setTaskToDelete(null);
      }
    },
    [deleteTask, taskToDelete]
  );

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteModalOpen(false);
    setTaskToDelete(null);
  }, []);

  const handleComplete = useCallback(
    async (taskId: string) => {
      const isConfirmed = window.confirm("Mark this task as completed?");
      if (!isConfirmed) return;

      try {
        const response = await axiosInstance.post(`/api/task/${taskId}/complete`, {});
        await fetchTasks();
        toast.success("Task completed successfully");
      } catch (error) {
        console.error("❌ Error completing task:", error);
        toast.error("Failed to complete task");
      }
    },
    [fetchTasks]
  );

  const getResponsiblePersonName = useCallback(
    (personId: string) => {
      const user = orgUsersData.find((u: any) => u._id === personId);
      return user ? `${user.firstName} ${user.lastName}` : personId || "N/A";
    },
    [orgUsersData]
  );

  const getTopicName = useCallback(
    (topicId: string) => {
      const topic = equipmentData.find((t: any) => t._id === topicId);
      return topic ? topic.equipmentName : topicId || "N/A";
    },
    [equipmentData]
  );

  const columnDefs = useMemo(
    (): ColDef<Task>[] => [
      {
        headerName: "Task",
        field: "task",
        flex: 2,
        minWidth: 220,
        headerClass: "bg-[#e9f5ff]",
      },
      {
        headerName: "Topic",
        field: "topic",
        flex: 1.4,
        minWidth: 150,
        valueGetter: (params) => getTopicName(params.data?.topic || ""),
        headerClass: "bg-[#e9f5ff]",
      },

      {
        headerName: "Premises",
        field: "premises",
        flex: 1.4,
        minWidth: 150,
        headerClass: "bg-[#e9f5ff]",
      },
      {
        headerName: "Frequency",
        field: "frequency",
        flex: 0.9,
        minWidth: 110,
        headerClass: "bg-[#e9f5ff]",
        cellStyle: {
          color: "#616263",
          fontWeight: "bold",
        },
      },
      {
        headerName: "Responsible Person",
        field: "reference",
        flex: 1.4,
        minWidth: 160,
        valueGetter: (params) =>
          getResponsiblePersonName(params.data?.reference || ""),
        headerClass: "bg-[#e9f5ff]",
      },
      {
        headerName: "Beginning Date",
        field: "fromDueDate",
        flex: 0.9,
        minWidth: 130,
        headerClass: "bg-[#e9f5ff]",
        cellStyle: { fontWeight: "bold" },
        valueFormatter: (params) =>
          formatBeginningDate(params.data?.fromDueDate),
      },
      {
        headerName: "Remaining Days Until Next Task",
        field: "daysRemaining",
        flex: 1.6,
        minWidth: 190,
        headerClass: "bg-[#e9f5ff]",
        cellStyle: (params) => {
          const daysRemaining = params.value || "N/A";
          const isRecurring = params.data?.isRecurring || false;
          const frequency = params.data?.frequency || "";

          // For one-time non-recurring tasks, show N/A
          if (!isRecurring && frequency === "One Time") {
            return {
              color: "#616263",
              fontWeight: "bold",
            };
          }

          if (daysRemaining === "N/A") {
            return {
              color: "#616263",
              fontWeight: "bold",
            };
          }

          const isOverdue =
            daysRemaining.includes("overdue") ||
            daysRemaining.includes("Overdue");
          const isDueToday =
            daysRemaining.includes("today") || daysRemaining.includes("Today");

          if (isOverdue) {
            return {
              color: "red",
              fontWeight: "bold",
            };
          } else if (isDueToday) {
            return {
              color: "orange",
              fontWeight: "bold",
            };
          } else {
            return {
              color: "green",
              fontWeight: "bold",
            };
          }
        },
        valueFormatter: (params) => {
          const isRecurring = params.data?.isRecurring || false;
          const frequency = params.data?.frequency || "";

          // For one-time non-recurring tasks, return N/A
          if (!isRecurring && frequency === "One Time") {
            return "N/A";
          }
          return params.value || "N/A";
        },
      },
      {
        headerName: "Next Task Date",
        field: "displayDate",
        flex: 0.9,
        minWidth: 130,
        headerClass: "bg-[#e9f5ff]",
        cellStyle: { fontWeight: "bold" },
        valueFormatter: (params) =>
          formatBeginningDate(params.data?.displayDate),
      },
      {
        headerName: "Status",
        field: "status",
        flex: 0.9,
        minWidth: 110,
        headerClass: "bg-[#e9f5ff] text-center",
        cellStyle: (params) => {
          const status = params.value;
          if (status === "Completed")
            return {
              color: "green",
              fontWeight: "bold",
            };
          if (status === "Overdue")
            return {
              color: "red",
              fontWeight: "bold",
            };
          return {
            color: "#f59e0b",
            fontWeight: "bold",
          };
        },
      },
      {
        headerName: "Actions",
        width: 120,
        minWidth: 100,
        cellRenderer: (params: any) => {
          const task = params.data as Task;
          return (
            <div className="flex gap-2">
              {canRead(TASK__MODULE) && (
                <button onClick={() => handleView(task)} title="View">
                  <img className="iconTd" src={images.viewIcon} alt="View" />
                </button>
              )}
              {canUpdate(TASK__MODULE) && (
                <button onClick={() => handleEdit(task)} title="Edit">
                  <img className="iconTd" src={images.editIcon} alt="Edit" />
                </button>
              )}
              {canDelete(TASK__MODULE) && (
                <button
                  onClick={() => handleDeleteClick(task)}
                  className="text-red-500 hover:text-red-700 text-sm"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              )}
              <button
                onClick={() => handleComplete(task.id)}
                className="text-purple-500 hover:text-purple-700 text-sm"
                title="Complete"
              >
                <CheckCircle size={16} />
              </button>
            </div>
          );
        },
      },
    ],
    [
      getTopicName,
      getResponsiblePersonName,
      formatBeginningDate,
      canRead,
      canUpdate,
      canDelete,
      handleView,
      handleEdit,
      handleDeleteClick,
      handleComplete,
      TASK__MODULE,
      images.viewIcon,
      images.editIcon,
    ]
  );

  const handleSearch = useCallback(
    (keyword: string) => {
      if (keyword) {
        searchTasks(keyword);
      } else {
        fetchTasks();
      }
    },
    [searchTasks, fetchTasks]
  );


  if (loading && tasks.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <DotLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-[#ffffff] rounded-md">
        <div className="text-red-500 text-center py-8">
          Error loading tasks: {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-[#ffffff] rounded-md shadow-md ">
        <div className="p-4 flex justify-between items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Search"
              onChange={(e) => handleSearch(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border-2 border-[#a5b7cf] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
            />
          </div>
          <div className="flex justify-center items-center space-x-4">
            <button
              className="bg-[#61748e] hover:bg-[#526177] text-white px-4 py-2 rounded-full flex items-center gap-2 transition-colors duration-200"
              onClick={() => setIsOpen(true)}
            >
              <img src={images.filter} width={20} />

              <span className="text-sm font-medium">
                FILTER{" "}
                {tasksWithFilter.length === 0 ? "(0)" : `(${filterCount})`}
              </span>
            </button>
            {tasksWithFilter.length > 0 && (
              <button
                className="bg-[#0fa9a6] hover:bg-[#0fa9a6] text-white px-4 py-2 rounded-full flex items-center gap-2 transition-colors duration-200"
                onClick={() => resetFilter()}
              >
                <span className="text-sm font-medium">CLEAR FILTERS</span>
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <SpinLoader />
          </div>
        ) : (
          <div className="ag-theme-alpine">
            <AgGridReact
              ref={gridRef}
              rowData={
                tasksWithFilter.length > 0
                  ? tasksWithFilter
                  : filteredTasks.length > 0 ||
                    selectedPremises ||
                    selectedModule
                    ? filteredTasks
                    : tasks
              }
              columnDefs={columnDefs}
              headerHeight={65}
              domLayout="autoHeight"
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[5, 10, 20]}
              paginationAutoPageSize={false}
              defaultColDef={{
                sortable: true,
                resizable: true,
                wrapText: true,
                autoHeight: true,
              }}
            />
          </div>
        )}
      </div>

      {isOpen && (
        <Filters
          showModal={isOpen}
          setShowModal={setIsOpen}
          tasks={tasks}
          setFilterCount={setFilterCount}
        />
      )}
      {isEditModalOpen && (
        <AddTask
          showModal={isEditModalOpen}
          setShowModal={setIsEditModalOpen}
          isEditMode={true}
        />
      )}
      {isViewModalOpen && (
        <AddTask
          showModal={isViewModalOpen}
          setShowModal={setIsViewModalOpen}
          isEditMode={false}
          isViewMode={true}
        />
      )}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title={`Are you sure you want to delete "${taskToDelete?.task || ''}"?`}
        confirmLabel="YES"
        cancelLabel="NO"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

export default TaskTable;
